import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Machine} from '@models/machine/machine';
import {MachineCopySettings} from '@models/machine/machine-copy-settings';
import {Gateway} from '@models/gateway/gateway';
import {Gateway as GatewayAdmin} from '@models/adminstration/gateway/gateway';
import {MachineAddProperties} from '@models/machine/machine-add-properties';
import {TableauProject} from '@models/machine/tableau-project';
import {MetaData} from '@models/adminstration/meta-data';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {TrafficLightConfiguration} from '@models/machine/traffic-light/traffic-light-configuration';
import {ChartConfig} from '@models/machine/chart-config';

@Injectable({
  providedIn: 'root'
})
export class MachineService {
  public MACHINE_DETAILS_URL_SUFFIX = '/api/web/admin/machine/:machineId';
  private MACHINE_LIST_URL_SUFFIX = '/api/web/admin/machine/list/:organizationId/:groupId/:limit/:cursor/:onlyTemplateMachines';
  private MACHINE_GET_WITHOUT_SIGNALS_URL_SUFFIX = '/api/web/admin/machine/:machineId/withoutSignals';
  private MACHINE_LIST_FOR_ORGANIZATION_URL_SUFFIX = '/api/web/admin/machine/listForOrganization/:tenantId';
  private MACHINE_LIST_CATEGORIES_URL_SUFFIX = '/api/web/admin/machine/categories';
  private MACHINE_LIST_TABLEAU_PROJECTS_URL_SUFFIX = '/api/web/admin/machine/:machineId/tableau/:tenantId/projects';
  private MACHINE_ADD_MACHINE_URL_SUFFIX = '/api/web/admin/machine';
  private MACHINE_ADD_MACHINE_FROM_TEMPLATE_URL_SUFFIX = '/api/web/admin/machine/createFromTemplate';
  private MACHINE_UPDATE_MACHINE_URL_SUFFIX = '/api/web/admin/machine/:id';
  private MACHINE_DELETE_MACHINE_URL_SUFFIX = '/api/web/admin/machine/:id';
  private MACHINE_LIST_GATEWAYS_URL_SUFFIX = '/api/web/admin/machine/:machineId/listGateways';
  private MACHINE_LIST_TABLES_URL_SUFFIX = '/api/web/admin/machine/:id/table';
  private MACHINE_LIST_SIGNALS_URL_SUFFIX = '/api/web/admin/machine/:id/getSignals';
  private MACHINE_GET_DEFAULT_PROJECT_ID = '/api/web/admin/machine/defaultProjectId';
  private MACHINE_IS_AVAILABLE_FOR_USER_URL_SUFFIX = '/api/web/admin/machine/:id/machineIsAvailableForUser';
  private MACHINE_UPDATE_TRAFFIC_LIGHT_CONFIGURATION_URL_SUFFIX = '/api/web/admin/machine/:id/updateTrafficLightConfiguration';
  private MACHINE_UPDATE_STATUS_INDICATOR_CONFIGURATION_URL_SUFFIX = '/api/web/admin/machine/:id/updateStatusIndicatorConfiguration';

  private MACHINE_METADATA_SAVE_URL_SUFFIX = '/api/web/admin/machine/:id/metadata/save';
  private MACHINE_METADATA_DELETE_URL_SUFFIX = '/api/web/admin/machine/:id/metadata/delete/:metaDataKey';


  private readonly BASE_URL: string;
  private googleProjectIdValue = new Map();

  constructor(
      private http: HttpClient,
      private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  private static isString(value: any) {
    return typeof value === 'string' || value instanceof String;
  }

  public list(organizationId: number, groupId: number, onlyTemplateMachines: boolean, limit: number, cursor: string): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_LIST_URL_SUFFIX;
    url = url.replace(':organizationId', String(organizationId))
    .replace(':groupId', String(groupId))
    .replace(':limit', String(limit))
    .replace(':cursor', cursor)
    .replace(':onlyTemplateMachines', String(onlyTemplateMachines));
    return this.http.get<Machine[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public listForOrganization(organizationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_LIST_FOR_ORGANIZATION_URL_SUFFIX;
    url = url.replace(':tenantId', String(organizationId));
    return this.http.get<Machine[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public listCategories(): Observable<any> {
    const url: string = this.BASE_URL + this.MACHINE_LIST_CATEGORIES_URL_SUFFIX;
    return this.http.get<string[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public listTableauProjects(machineId: number, tenantId: number): Observable<TableauProject[]> {
    let url: string = this.BASE_URL + this.MACHINE_LIST_TABLEAU_PROJECTS_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId));
    url = url.replace(':tenantId', String(tenantId));
    return this.http.get<TableauProject[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public addMachine(copy: boolean, copySettings: MachineCopySettings, group: any, machine: MachineAddProperties, sourceMachineId: number | undefined, firebaseUserId: string): Observable<any> {
    const request = {
      copy,
      copySettings,
      group,
      sourceMachineId,
      machine,
      firebaseUserId
    };

    if (sourceMachineId == undefined) {
      delete request.sourceMachineId;
    }

    const url: string = this.BASE_URL + this.MACHINE_ADD_MACHINE_URL_SUFFIX;
    return this.http.post<Machine[]>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public addMachineFromTemplate(group: any, machine: MachineAddProperties, gateway: GatewayAdmin): Observable<any> {
    const request = {
      groups: [group],
      gateway: gateway,
      machine: machine
    };

    const url: string = this.BASE_URL + this.MACHINE_ADD_MACHINE_FROM_TEMPLATE_URL_SUFFIX;
    return this.http.post<Machine[]>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public updateMachine(machine: Machine): Observable<any> {
    const request = {
      category: machine.category,
      chartConfig: machine.chartConfig,
      dataSourcePartitioning: machine.dataSourcePartitioning,
      datasetLocation: machine.datasetLocation,
      id: machine.id,
      location: machine.location,
      locationDetails: machine.locationDetails,
      machineCategoryId: machine.machineCategoryId,
      machineTag: machine.machineTag,
      name: machine.name,
      parentId: machine.parentId,
      projectId: machine.projectId,
      signals: machine.signals,
      tableauProjectId: machine.tableauProjectId,
      trafficLightConfiguration: machine.trafficLightConfiguration,
      type: machine.type,
      externalId: machine.externalId,
      parentExternalId: machine.parentExternalId,
      templateMachine: machine.templateMachine
    };
    let url: string = this.BASE_URL + this.MACHINE_UPDATE_MACHINE_URL_SUFFIX;
    url = url.replace(':id', String(machine.id));
    return this.http.post<Machine[]>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public delete(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_DELETE_MACHINE_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public listGateways(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_LIST_GATEWAYS_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId));
    return this.http.get<Gateway[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public listTables(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_LIST_TABLES_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get<string[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public getSignals(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_LIST_SIGNALS_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getMachineConfiguration(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_DETAILS_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId));
    return this.http.get<Machine[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public getMachineWithoutSignals(machineId: string): Observable<any> {
    if (machineId) {
      let url: string = this.BASE_URL + this.MACHINE_GET_WITHOUT_SIGNALS_URL_SUFFIX;
      url = url.replace(':machineId', machineId);
      return this.http.get<Machine[]>(url, HTTPS_REQUEST_OPTIONS);
    }
    return of(null);
  }

  public getDefaultGoogleProjectId(): Observable<any> {
    const url: string = this.BASE_URL + this.MACHINE_GET_DEFAULT_PROJECT_ID;
    const defaultProjectIdFromCache = this.googleProjectIdValue.get(url);
    if (defaultProjectIdFromCache) {
      return of(defaultProjectIdFromCache);
    }
    const response = this.http.get<string[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true,
    }));
    response.subscribe((result) => {
      this.googleProjectIdValue.set(url, result);
    });

    return response;
  }


  public saveMetaData(machineId: string, metaData: MetaData): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_METADATA_SAVE_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.post(url, metaData, HTTPS_REQUEST_OPTIONS);
  }


  public deleteMetaData(machineId: string, deleteMetaData: MetaData): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_METADATA_DELETE_URL_SUFFIX;
    url = url.replace(':id', machineId).replace(':metaDataKey', String(deleteMetaData.key));

    return this.http.delete<MetaData>(url, HTTPS_REQUEST_OPTIONS);
  }

  public isNullOrEmptyOrWhiteSpace(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    return MachineService.isString(value) && value.replace(/\s/g, '') === '';
  }

  public machineIsAvailableForUser(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_IS_AVAILABLE_FOR_USER_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveTrafficLightConfiguration(machineId: number, trafficLightConfig: TrafficLightConfiguration): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_UPDATE_TRAFFIC_LIGHT_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.post(url, trafficLightConfig, HTTPS_REQUEST_OPTIONS);
  }

  public saveStatusIndicatorConfiguration(machineId: number, chartConfig: ChartConfig): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_UPDATE_STATUS_INDICATOR_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.post(url, chartConfig, HTTPS_REQUEST_OPTIONS);
  }

}
